import imageCompression from 'browser-image-compression';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';

export interface CompressionOptions {
  level: 'fast' | 'balanced' | 'quality';
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  onProgress?: (progress: number) => void;
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  savings: string;
  duration: number;
}

export class CompressionService {
  private static instance: CompressionService;

  private constructor() {}

  static getInstance(): CompressionService {
    if (!CompressionService.instance) {
      CompressionService.instance = new CompressionService();
    }
    return CompressionService.instance;
  }

  /**
   * Main compression method that detects file type and applies appropriate compression
   */
  async compressFile(
    file: File, 
    options: CompressionOptions = { level: 'balanced' }
  ): Promise<CompressionResult> {
    const startTime = Date.now();
    const originalSize = file.size;

    // Detect file type and apply appropriate compression
    const fileType = this.detectFileType(file);
    let compressedFile: File;

    try {
      switch (fileType) {
        case 'pdf':
          // PDFs are already internally compressed and binary-structured.
          // Running them through pdf-lib (especially with ignoreEncryption: true) can
          // corrupt encryption metadata and cause Chrome's PDF viewer to fail mid-render
          // even though the file appears structurally valid (passes %PDF-x.x / %%EOF checks).
          // Pass PDFs through unchanged — no size benefit and high breakage risk.
          compressedFile = file;
          break;
        case 'image':
          compressedFile = await this.compressImage(file, options);
          break;
        case 'document':
          compressedFile = await this.compressDocument(file, options);
          break;
        default:
          // No compression for unknown types
          compressedFile = file;
      }

      const compressedSize = compressedFile.size;
      const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;
      const savings = this.formatSavings(originalSize, compressedSize);
      const duration = Date.now() - startTime;

      return {
        file: compressedFile,
        originalSize,
        compressedSize,
        compressionRatio,
        savings,
        duration
      };
    } catch (error) {
      console.error('Compression failed:', error);
      // Return original file if compression fails
      return {
        file,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 0,
        savings: 'No compression',
        duration: Date.now() - startTime
      };
    }
  }

  /**
   * Compress PDF files
   */
  private async compressPDF(file: File, options: CompressionOptions): Promise<File> {
    try {
      // Read the PDF file
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      
      // Get compression settings based on level
      const settings = this.getPDFCompressionSettings(options.level);
      
      // Report progress
      options.onProgress?.(20);

      // Remove metadata if aggressive compression
      if (settings.removeMetadata) {
        pdfDoc.setTitle('');
        pdfDoc.setAuthor('');
        pdfDoc.setSubject('');
        pdfDoc.setKeywords([]);
        pdfDoc.setProducer('');
        pdfDoc.setCreator('');
      }

      options.onProgress?.(40);

      // Get pages and process embedded images
      const pages = pdfDoc.getPages();
      
      // For aggressive compression, we could potentially reduce image quality
      // Note: pdf-lib doesn't provide direct image manipulation, so we focus on structure optimization
      
      options.onProgress?.(60);

      // Save the PDF with compression
      const pdfBytes = await pdfDoc.save({
        useObjectStreams: settings.useObjectStreams,
        addDefaultPage: false,
        objectsPerTick: 50
      });

      options.onProgress?.(80);

      // Create a new file from the compressed bytes
      const compressedFile = new File(
        [pdfBytes], 
        file.name, 
        { type: 'application/pdf' }
      );

      options.onProgress?.(100);

      return compressedFile;
    } catch (error) {
      console.warn('PDF compression failed, using original file:', error);
      // Return original file instead of throwing
      return file;
    }
  }

  /**
   * Compress image files
   */
  private async compressImage(file: File, options: CompressionOptions): Promise<File> {
    const settings = this.getImageCompressionSettings(file, options.level);
    
    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: settings.maxSizeMB,
        maxWidthOrHeight: settings.maxWidthOrHeight,
        useWebWorker: options.useWebWorker ?? true,
        onProgress: options.onProgress,
        fileType: settings.outputType,
        initialQuality: settings.quality
      });

      // If converting PNG to JPEG, rename the file
      if (settings.convertToJpeg && file.name.toLowerCase().endsWith('.png')) {
        const newName = file.name.replace(/\.png$/i, '.jpg');
        return new File([compressedFile], newName, { type: 'image/jpeg' });
      }

      return compressedFile;
    } catch (error) {
      console.warn('Image compression failed, using original file:', error);
      // Return original file instead of throwing
      return file;
    }
  }

  /**
   * Compress document files (DOCX, XLSX, etc.)
   */
  private async compressDocument(file: File, options: CompressionOptions): Promise<File> {
    try {
      const zip = new JSZip();
      const arrayBuffer = await file.arrayBuffer();
      
      // Load the document (which is actually a zip file)
      await zip.loadAsync(arrayBuffer);
      
      options.onProgress?.(30);

      // Compress images within the document
      const imagePromises: Promise<void>[] = [];
      
      zip.forEach((relativePath, zipEntry) => {
        // Check if it's an image
        if (/\.(jpg|jpeg|png|gif|bmp)$/i.test(relativePath)) {
          imagePromises.push(
            zipEntry.async('blob').then(async (blob) => {
              const imageFile = new File([blob], relativePath);
              const compressed = await this.compressImage(imageFile, {
                ...options,
                onProgress: undefined // Don't report individual image progress
              });
              
              // Replace the image in the zip
              zip.file(relativePath, compressed);
            })
          );
        }
      });

      await Promise.all(imagePromises);
      
      options.onProgress?.(70);

      // Generate the compressed document
      const compressed = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: options.level === 'fast' ? 1 : options.level === 'quality' ? 9 : 6
        }
      });

      options.onProgress?.(100);

      return new File([compressed], file.name, { type: file.type });
    } catch (error) {
      console.warn('Document compression failed, using original file:', error);
      // Return original file instead of throwing
      return file;
    }
  }

  /**
   * Get PDF compression settings based on level
   */
  private getPDFCompressionSettings(level: 'fast' | 'balanced' | 'quality') {
    switch (level) {
      case 'fast':
        return {
          removeMetadata: true,
          useObjectStreams: true,
          compressStreams: true,
          imageQuality: 0.5
        };
      case 'quality':
        return {
          removeMetadata: false,
          useObjectStreams: true,
          compressStreams: true,
          imageQuality: 0.9
        };
      case 'balanced':
      default:
        return {
          removeMetadata: true,
          useObjectStreams: true,
          compressStreams: true,
          imageQuality: 0.7
        };
    }
  }

  /**
   * Get image compression settings based on level and use case
   */
  private getImageCompressionSettings(file: File, level: 'fast' | 'balanced' | 'quality') {
    const isProfilePhoto = file.name.toLowerCase().includes('profile') || 
                          file.name.toLowerCase().includes('avatar');
    const isPNG = file.type === 'image/png';
    
    // Check if PNG should be converted to JPEG (if not transparent)
    const convertToJpeg = isPNG && level !== 'quality';

    switch (level) {
      case 'fast':
        return {
          maxSizeMB: isProfilePhoto ? 0.3 : 0.5,
          maxWidthOrHeight: isProfilePhoto ? 600 : 1280,
          quality: 0.6,
          convertToJpeg,
          outputType: convertToJpeg ? 'image/jpeg' : file.type
        };
      case 'quality':
        return {
          maxSizeMB: isProfilePhoto ? 0.8 : 2,
          maxWidthOrHeight: isProfilePhoto ? 1000 : 1920,
          quality: 0.9,
          convertToJpeg: false,
          outputType: file.type
        };
      case 'balanced':
      default:
        return {
          maxSizeMB: isProfilePhoto ? 0.5 : 1,
          maxWidthOrHeight: isProfilePhoto ? 800 : 1600,
          quality: 0.8,
          convertToJpeg,
          outputType: convertToJpeg ? 'image/jpeg' : file.type
        };
    }
  }

  /**
   * Detect file type from file object
   */
  private detectFileType(file: File): 'pdf' | 'image' | 'document' | 'unknown' {
    const mimeType = file.type.toLowerCase();
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    if (mimeType === 'application/pdf' || extension === 'pdf') {
      return 'pdf';
    }
    
    if (mimeType.startsWith('image/') || 
        ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
      return 'image';
    }
    
    if (mimeType.includes('officedocument') || 
        mimeType.includes('msword') ||
        mimeType.includes('ms-excel') ||
        ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(extension)) {
      return 'document';
    }

    return 'unknown';
  }

  /**
   * Format savings message
   */
  private formatSavings(originalSize: number, compressedSize: number): string {
    const savedBytes = originalSize - compressedSize;
    const savedPercent = Math.round((savedBytes / originalSize) * 100);
    
    if (savedPercent <= 0) {
      return 'No compression needed';
    }

    const formatSize = (bytes: number): string => {
      if (bytes < 1024) return `${bytes} bytes`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return `Saved ${formatSize(savedBytes)} (${savedPercent}%)`;
  }

  /**
   * Batch compress multiple files
   */
  async compressFiles(
    files: File[], 
    options: CompressionOptions = { level: 'balanced' }
  ): Promise<CompressionResult[]> {
    const results: CompressionResult[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileProgress = (progress: number) => {
        // Calculate overall progress
        const overallProgress = ((i / files.length) + (progress / 100 / files.length)) * 100;
        options.onProgress?.(overallProgress);
      };
      
      const result = await this.compressFile(file, {
        ...options,
        onProgress: fileProgress
      });
      
      results.push(result);
    }
    
    return results;
  }

  /**
   * Estimate compression without actually compressing
   */
  estimateCompression(file: File, level: 'fast' | 'balanced' | 'quality' = 'balanced'): {
    estimatedSize: number;
    estimatedSavings: string;
    estimatedRatio: number;
  } {
    const fileType = this.detectFileType(file);
    let estimatedRatio = 0;

    // Estimate based on file type and compression level
    switch (fileType) {
      case 'pdf':
        estimatedRatio = level === 'fast' ? 0.6 : level === 'quality' ? 0.3 : 0.45;
        break;
      case 'image':
        // PNG files compress more than JPEGs
        if (file.type === 'image/png') {
          estimatedRatio = level === 'fast' ? 0.7 : level === 'quality' ? 0.4 : 0.55;
        } else {
          estimatedRatio = level === 'fast' ? 0.5 : level === 'quality' ? 0.2 : 0.35;
        }
        break;
      case 'document':
        estimatedRatio = level === 'fast' ? 0.4 : level === 'quality' ? 0.2 : 0.3;
        break;
      default:
        estimatedRatio = 0;
    }

    const estimatedSize = Math.round(file.size * (1 - estimatedRatio));
    const estimatedSavings = this.formatSavings(file.size, estimatedSize);

    return {
      estimatedSize,
      estimatedSavings,
      estimatedRatio: estimatedRatio * 100
    };
  }
}

// Export singleton instance
export const compressionService = CompressionService.getInstance();